"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

declare global {
  interface Window {
    lipscore?: {
      init?: (cfg: { apiKey: string }) => void;
      initWidgets?: () => void;
      reInitWidgets?: (force?: boolean) => void;
    };
  }
}

type Props = { apiKey: string };

export default function LipscoreProvider({ apiKey }: Props) {
  const pathname = usePathname();
  const scriptLoaded = useRef(false);

  useEffect(() => {
    const loadScript = () => {
      const id = "lipscore-script";
      if (document.getElementById(id)) {
        scriptLoaded.current = true;
        cleanup();
        return;
      }

      const scr = document.createElement("script");
      scr.id = id;
      scr.async = true;
      scr.src = "https://static.lipscore.com/assets/no/lipscore-v1.js";
      scr.onload = () => {
        scriptLoaded.current = true;
        window.lipscore?.init?.({ apiKey });
        window.lipscore?.reInitWidgets?.(true) ??
          window.lipscore?.initWidgets?.();
      };
      scr.onerror = () => console.error("Failed to load Lipscore");
      document.head.appendChild(scr);
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener("click", loadScript);
      window.removeEventListener("scroll", loadScript);
      window.removeEventListener("keydown", loadScript);
    };

    window.addEventListener("click", loadScript, { once: true });
    window.addEventListener("scroll", loadScript, { once: true });
    window.addEventListener("keydown", loadScript, { once: true });

    return cleanup;
  }, [apiKey]);

  useEffect(() => {
    if (scriptLoaded.current) {
      window.lipscore?.reInitWidgets?.(true) ??
        window.lipscore?.initWidgets?.();
    }
  }, [pathname]);

  return null;
}
